import React, { useState, useEffect } from 'react';
import {
  Box,
  Container, 
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  MenuItem as SelectMenuItem,
  InputAdornment,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Vademecum = ({ isDrawerContext = false }) => { 
  const navigate = useNavigate();
  const [medicamentos, setMedicamentos] = useState([]);
  const [pets, setPets] = useState([]); 
  const [formData, setFormData] = useState({
    id_pet: null,
    nombre_medicamento: '',
    descripcion_medicamento: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 // const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [userSucursales, setUserSucursales] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingMedicamentoId, setEditingMedicamentoId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Decodificar token JWT y cargar datos
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
       // setUserIdPet(payload.id_pet || null);
        setUserIdRol(payload.id_rol || null);
        setUserSucursales(payload.sucursales || []);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        const errorMsg = 'Error al decodificar el token de usuario';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        try {
          const petsRes = await api.get('/pets/');
          setPets(petsRes.data);
        } catch (petError) {
          console.warn('Error al cargar veterinarias, usando datos vacíos');
          setPets([]);
        }
        try {
          const medicamentosRes = await api.get('/medicamentos/');
          setMedicamentos(medicamentosRes.data);
        } catch (medError) {
          console.warn('Error al cargar medicamentos, usando datos vacíos');
          setMedicamentos([]);
        }
        setLoading(false);
      } catch (error) {
        setError('Error al cargar datos: ' + (error.response?.data?.detail || error.message));
        setLoading(false);
        setSnackbarMessage('Error al cargar datos: ' + (error.response?.data?.detail || error.message));
        setSnackbarOpen(true);
      }
    };
    fetchData();
  }, []);

  const filteredMedicamentos = medicamentos.filter((medicamento) =>
    medicamento.nombre_medicamento.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (medicamento.descripcion_medicamento && medicamento.descripcion_medicamento.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const truncateDescription = (descripcion) => {
    if (!descripcion) return 'N/A';
    return descripcion.length > 50 ? `${descripcion.slice(0, 50)}...` : descripcion;
  };

  const handleCopyNombre = (nombre) => {
    navigator.clipboard.writeText(nombre);
    setSnackbarMessage(`Nombre "${nombre}" copiado al portapapeles`);
    setSnackbarOpen(true);
  };

  const handleCopyNombreYDescripcion = (nombre, descripcion) => {
    const texto = `${nombre}${descripcion ? ` - ${descripcion}` : ''}`;
    navigator.clipboard.writeText(texto);
    setSnackbarMessage(`"${texto}" copiado al portapapeles`);
    setSnackbarOpen(true);
  };

  // Guardar medicamento (crear o actualizar)
  const handleSave = async (datosParaGuardar = formData) => {
    try {
      if (!datosParaGuardar.nombre_medicamento.trim()) {
        setError('El nombre del medicamento es obligatorio');
        setSnackbarMessage('El nombre del medicamento es obligatorio');
        setSnackbarOpen(true);
        return;
      }
      if (!datosParaGuardar.id_pet || isNaN(datosParaGuardar.id_pet)) {
        setError('La veterinaria es obligatoria');
        setSnackbarMessage('La veterinaria es obligatoria');
        setSnackbarOpen(true);
        return;
      }
      if (userIdRol !== '1' && userIdRol !== 'SYSTEM_USER' && !userSucursales.includes(parseInt(datosParaGuardar.id_pet))) {
        setError('No tienes permiso para esta veterinaria');
        setSnackbarMessage('No tienes permiso para esta veterinaria');
        setSnackbarOpen(true);
        return;
      }

      const payload = {
          id_pet: parseInt(datosParaGuardar.id_pet), 
          nombre_medicamento: datosParaGuardar.nombre_medicamento,
          descripcion_medicamento: datosParaGuardar.descripcion_medicamento || null,
      };

      if (editingMedicamentoId) {
        const res = await api.put(`/medicamentos/${editingMedicamentoId}`, payload);
        setMedicamentos(medicamentos.map(m => m.id_medicamento === editingMedicamentoId ? res.data : m));
        setSnackbarMessage('Medicamento actualizado');
      } else {
        const res = await api.post('/medicamentos/', payload);
        setMedicamentos([...medicamentos, res.data]);
        setSnackbarMessage('Medicamento creado');
      }
      setOpenModal(false);
      setEditingMedicamentoId(null);
      setFormData({ id_pet: null, nombre_medicamento: '', descripcion_medicamento: '' });
      setError(null);
      setSnackbarOpen(true);
    } catch (error) {
      const errorMsg = 'Error al guardar medicamento: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
      if (error.response?.status === 401 && !isDrawerContext) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  };

  const handleEdit = (medicamento) => {
    setFormData({
      id_pet: medicamento.id_pet,
      nombre_medicamento: medicamento.nombre_medicamento,
      descripcion_medicamento: medicamento.descripcion_medicamento || '',
    });
    setEditingMedicamentoId(medicamento.id_medicamento);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este medicamento?')) {
      try {
        await api.delete(`/medicamentos/${id}`);
        setMedicamentos(medicamentos.filter(m => m.id_medicamento !== id));
        setSnackbarMessage('Medicamento eliminado');
        setSnackbarOpen(true);
      } catch (error) {
        const errorMsg = 'Error al eliminar medicamento: ' + (error.response?.data?.detail || error.message);
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        if (error.response?.status === 401 && !isDrawerContext) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const VademecumContent = (
    <Box sx={{ p: isDrawerContext ? 1 : 0, minHeight: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Vademécum</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isDrawerContext && (
            <Button variant="contained" onClick={() => navigate('/')}>
              Regresar
            </Button>
          )}
          <Button variant="contained" onClick={() => setOpenModal(true)}>
            Agregar Medicamento
          </Button>
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Todos los Medicamentos</Typography>
          <TextField
            label="Buscar medicamento o descripción"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: '300px' }}
          />
        </Box>
        {filteredMedicamentos.length > 0 ? (
          <Box 
            sx={{ maxHeight: isDrawerContext ? 'calc(100vh - 200px)' : '400px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}
          >
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMedicamentos.map((medicamento) => (
                  <TableRow key={medicamento.id_medicamento}>
                    <TableCell>{medicamento.nombre_medicamento || 'N/A'}</TableCell>
                    <TableCell>
                      <Tooltip title={medicamento.descripcion_medicamento || 'N/A'}>
                        <span>{truncateDescription(medicamento.descripcion_medicamento)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Copiar nombre">
                        <IconButton onClick={() => handleCopyNombre(medicamento.nombre_medicamento)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copiar nombre y descripción">
                        <IconButton onClick={() => handleCopyNombreYDescripcion(medicamento.nombre_medicamento, medicamento.descripcion_medicamento)}>
                          <DescriptionIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => handleEdit(medicamento)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton onClick={() => handleDelete(medicamento.id_medicamento)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography sx={{ mt: 2 }}>No hay medicamentos que coincidan con la búsqueda</Typography>
        )}
      </Box>

      {/* 💡 AQUÍ USAMOS EL COMPONENTE HIJO OPTIMIZADO PARA DETENER LA LENTITUD */}
      {/* 💡 Solo monta el componente en memoria cuando realmente se va a abrir */}
      {openModal && (
      <MedicamentoFormDialog
        open={openModal}
        formData={formData}
        pets={pets}
        userIdRol={userIdRol}
        userSucursales={userSucursales}
        onClose={() => {
          setOpenModal(false);
          setEditingMedicamentoId(null);
          setFormData({ id_pet: null, nombre_medicamento: '', descripcion_medicamento: '' });
        }}
        onSubmit={(datosActualizados) => {
          handleSave(datosActualizados);
        }}
      />
     )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1700 }} // Subido a 1700 para que se vea por encima del modal de 1200
      >
        <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );

  if (error && !snackbarOpen) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (isDrawerContext) {
    return VademecumContent; 
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      {VademecumContent}
    </Container>
  );
};

// ─── COMPONENTE HIJO CON ESTADO LOCAL PARA ESCRIBIR A MÁXIMA VELOCIDAD ───
// ─── COMPONENTE HIJO CON ESTADO LOCAL PARA ESCRIBIR A MÁXIMA VELOCIDAD ───
//const MedicamentoFormDialog = ({ open, onClose, onSubmit, formData: initialData, pets, userIdRol, userSucursales }) => {
const MedicamentoFormDialog = React.memo(({ open, onClose, onSubmit, formData: initialData, pets, userIdRol, userSucursales }) => {  
  const [localFormData, setLocalFormData] = useState(initialData);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md')); // Pantalla completa en tablets/móviles

  // Sincroniza el formulario cuando el padre cambia entre Crear o Editar
  useEffect(() => {
    if (open) {
      setLocalFormData(initialData);
    }
  }, [initialData, open]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      sx={{ zIndex: 1300 }}
      fullWidth          
      maxWidth="lg"
    >
      <DialogTitle>
        {initialData.id_medicamento || (localFormData.nombre_medicamento && initialData.nombre_medicamento) 
          ? 'Editar Medicamento / Dieta' 
          : 'Agregar Medicamento / Dieta'}
      </DialogTitle>
      
      <DialogContent>
        <TextField
          select
          label="Veterinaria"
          fullWidth
          value={localFormData.id_pet || ''}
          onChange={(e) => setLocalFormData({ ...localFormData, id_pet: e.target.value })}
          sx={{ mt: 2 }}
          required
        >
          {pets.length > 0 ? (
            pets
              .filter(pet => userIdRol === '1' || userIdRol === 'SYSTEM_USER' || userSucursales.includes(pet.id_pet))
              .map((pet) => (
                <SelectMenuItem key={pet.id_pet} value={pet.id_pet}>
                  {pet.nombre_veterinaria}
                </SelectMenuItem>
              ))
          ) : (
            <SelectMenuItem value="" disabled>
              No hay veterinarias disponibles
            </SelectMenuItem>
          )}
        </TextField>

        <TextField
          label="Nombre del Medicamento / Plan Nutricional"
          fullWidth
          value={localFormData.nombre_medicamento}
          onChange={(e) => setLocalFormData({ ...localFormData, nombre_medicamento: e.target.value })}
          sx={{ mt: 2 }}
          required
          placeholder="Ej: Dieta Renal Avanzada o Amoxicilina 250mg"
        />

        <Box sx={{ position: 'relative', mt: 2 }}>
          <TextField
            label="Descripción / Dieta Detallada"
            fullWidth
            multiline
            //rows={12}
            minRows={12}    // 👈 Subido a 12 para dar un golpe de vista inicial más imponente
            maxRows={22}
            value={localFormData.descripcion_medicamento || ''}
            onChange={(e) => setLocalFormData({ ...localFormData, descripcion_medicamento: e.target.value })}
            placeholder="Ejemplo de formato:&#10;• Mañana (08:00): 100g alimento húmedo medicado.&#10;• Tarde (14:00): 50g croquetas + 5ml suplemento.&#10;• Restricciones: Evitar totalmente snacks altos en sodio."
            inputProps={{
              style: { 
                fontFamily: 'Lucida Console, Courier, monospace', // 👈 Fuente monoespaciada para organizar listas y dosis
                fontSize: '14px',
                lineHeight: '1.5'
              }
            }}
          />
          
          {/* 👈 Botón rápido para limpiar textos gigantes en 1 click */}
          {localFormData.descripcion_medicamento && (
            <Button 
              size="small" 
              color="error"
              onClick={() => setLocalFormData({ ...localFormData, descripcion_medicamento: '' })}
              sx={{ 
                position: 'absolute', 
                top: 10, 
                right: 8, 
                zIndex: 10,
                backgroundColor: 'rgba(255,255,255,0.9)',
                '&:hover': { backgroundColor: '#fff' }
              }}
            >
              Limpiar texto
            </Button>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button
          onClick={() => onSubmit(localFormData)}
          variant="contained"
          disabled={!localFormData.nombre_medicamento || !localFormData.id_pet}
        >
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default Vademecum;